import { useState, useRef } from 'react';
import { Plus, Trash2, Pencil, GripVertical, Shield, Sparkles } from 'lucide-react';
import { Tooltip } from '@mui/material';
import GenericTable from '../../../../components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from '../../../../components/table/GenericTable.design';
import { CHAMADOS_CONFIG_DESIGN } from './ChamadosConfig.design';
import ModalPrioridadeChamado from './ModalPrioridadeChamado';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import {
  actionCriarPrioridade,
  actionEditarPrioridade,
  actionDeletarPrioridade,
  actionReordenarPrioridades,
} from 'src/actions/chamados/actionChamadosConfig';
import { Badge } from 'src/@/components/ui/badge';
import Iconify from 'src/components/Iconify';

const TABLE_HEAD = [
  { id: 'ordem', label: '', className: 'w-10' },
  { id: 'titulo', label: 'Titulo', className: 'flex-1' },
  { id: 'tempo_aceite', label: 'Tempo Aceite (min)', className: 'w-36 text-center' },
  { id: 'tempo_conclusao', label: 'Tempo Conclusao (min)', className: 'w-40 text-center' },
  { id: 'tipo', label: 'Tipo', className: 'w-32 text-center' },
  { id: 'actions', label: 'Acoes', className: 'w-24 text-right' },
];

export default function PrioridadesTab({ listaPrioridades, onRefresh, clienteId, caixaEntradaId }) {
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(false);
  const [prioridadeId, setPrioridadeId] = useState('');
  const [titulo, setTitulo] = useState('');
  const [cor, setCor] = useState('#7C3AED');
  const [tempoAceite, setTempoAceite] = useState('');
  const [tempoConclusao, setTempoConclusao] = useState('');

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const totais = {
    total: listaPrioridades.length,
    padrao: listaPrioridades.filter(p => p.tipo === 'padrao').length,
    custom: listaPrioridades.filter(p => p.tipo === 'custom').length,
  };

  function resetForm() {
    setTitulo('');
    setCor('#7C3AED');
    setTempoAceite('');
    setTempoConclusao('');
    setPrioridadeId('');
    setAction('');
  }

  async function handleCriar() {
    try {
      setLoading(true);
      await actionCriarPrioridade({
        titulo,
        cor,
        cliente_id: clienteId,
        caixa_entrada_id: caixaEntradaId,
        tempo_aceite: tempoAceite ? Number(tempoAceite) : undefined,
        tempo_conclusao: tempoConclusao ? Number(tempoConclusao) : undefined,
      });
      await onRefresh();
      setOpenModal(false);
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleEditar() {
    try {
      setLoading(true);
      await actionEditarPrioridade(prioridadeId, {
        titulo,
        cor,
        tempo_aceite: tempoAceite ? Number(tempoAceite) : undefined,
        tempo_conclusao: tempoConclusao ? Number(tempoConclusao) : undefined,
      });
      await onRefresh();
      setOpenModal(false);
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeletar() {
    try {
      setLoading(true);
      await actionDeletarPrioridade(prioridadeId);
      await onRefresh();
      setOpenConfirmacao(false);
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDragEnd() {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const reordered = [...listaPrioridades];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);
    dragItem.current = null;
    dragOverItem.current = null;

    try {
      await actionReordenarPrioridades({ ids: reordered.map(p => p._id) });
      await onRefresh();
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Action button */}
      <div className="flex justify-end">
        <button
          onClick={() => { setOpenModal(true); setAction('Criar'); }}
          className={CHAMADOS_CONFIG_DESIGN.page.actionButton}
        >
          <Plus size={18} />
          <span>Criar Prioridade</span>
        </button>
      </div>

      {/* Modals */}
      <ModalConfimacao
        open={openConfirmacao}
        onClose={() => setOpenConfirmacao(false)}
        onConfirm={handleDeletar}
        loadingFinalizar={loading}
        mensagem="Deseja mesmo excluir essa prioridade?"
      />
      <ModalPrioridadeChamado
        open={openModal}
        action={action}
        titulo={titulo}
        changeTitulo={(e) => setTitulo(e.target.value)}
        cor={cor}
        changeCor={(value) => setCor(value)}
        tempoAceite={tempoAceite}
        changeTempoAceite={(e) => setTempoAceite(e.target.value)}
        tempoConclusao={tempoConclusao}
        changeTempoConclusao={(e) => setTempoConclusao(e.target.value)}
        onSubmit={action === 'Criar' ? handleCriar : handleEditar}
        onClose={() => { setOpenModal(false); resetForm(); }}
      />

      {/* Table */}
      {listaPrioridades.length === 0 ? (
        <div className={CHAMADOS_CONFIG_DESIGN.emptyState.container}>
          <div className={CHAMADOS_CONFIG_DESIGN.emptyState.iconBox}>
            <Shield size={32} />
          </div>
          <h3 className={CHAMADOS_CONFIG_DESIGN.emptyState.title}>Nenhuma prioridade encontrada</h3>
          <p className={CHAMADOS_CONFIG_DESIGN.emptyState.description}>
            As prioridades padrao serao criadas automaticamente ao carregar a pagina.
          </p>
        </div>
      ) : (
        <GenericTable headLabels={TABLE_HEAD}>
          {listaPrioridades.map((row, index) => (
            <div
              key={row._id}
              className={`${GENERIC_TABLE_DESIGN.row.container} cursor-grab`}
              draggable
              onDragStart={() => { dragItem.current = index; }}
              onDragEnter={() => { dragOverItem.current = index; }}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
            >
              {/* Drag handle */}
              <div className="w-10 flex items-center text-gray-300">
                <GripVertical size={16} />
              </div>

              {/* Titulo */}
              <div className="flex-1 min-w-[200px] pr-4">
                <div className="flex items-center gap-3">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: row.cor }}
                  />
                  <span className="text-sm font-semibold text-gray-900">{row.titulo}</span>
                </div>
              </div>

              {/* Tempo Aceite */}
              <div className="w-36 text-center text-sm text-gray-600">
                {row.tempo_aceite ? `${row.tempo_aceite} min` : '-'}
              </div>

              {/* Tempo Conclusao */}
              <div className="w-40 text-center text-sm text-gray-600">
                {row.tempo_conclusao ? `${row.tempo_conclusao} min` : '-'}
              </div>

              {/* Tipo */}
              <div className="w-32 text-center flex justify-center">
                {row.tipo === 'padrao' ? (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-blue-50 text-blue-700 border-blue-200">
                    <Shield size={10} />
                    Padrao
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-violet-50 text-violet-700 border-violet-200">
                    <Sparkles size={10} />
                    Custom
                  </Badge>
                )}
              </div>

              {/* Actions */}
              <div className="w-24 flex justify-end gap-1">
                <Tooltip title="Editar">
                  <button
                    onClick={() => {
                      setOpenModal(true);
                      setAction('Editar');
                      setPrioridadeId(row._id);
                      setTitulo(row.titulo);
                      setCor(row.cor);
                      setTempoAceite(row.tempo_aceite || '');
                      setTempoConclusao(row.tempo_conclusao || '');
                    }}
                    className="p-2 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-all"
                  >
                    <Pencil size={18} />
                  </button>
                </Tooltip>
                <Tooltip title={row.tipo === 'padrao' ? 'Prioridade padrao nao pode ser excluida' : 'Excluir'}>
                  <span>
                    <button
                      onClick={() => {
                        if (row.tipo === 'padrao') return;
                        setPrioridadeId(row._id);
                        setOpenConfirmacao(true);
                      }}
                      disabled={row.tipo === 'padrao'}
                      className={`${GENERIC_TABLE_DESIGN.common.deleteButton} ${row.tipo === 'padrao' ? 'opacity-30 cursor-not-allowed' : ''}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </span>
                </Tooltip>
              </div>
            </div>
          ))}
        </GenericTable>
      )}
    </div>
  );
}
